import { type ComponentType, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import type { SvgIconProps } from "@mui/material";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { MODULE_STATIC_CONFIG } from "@src/components/BillingSettings2026/workflow";
import { type CategoryKey } from "@src/components/Dialogs/CreateExperienceDialog/types";
import {
  SMART_CHECKOUT_ID,
  SMART_CONTENT_ID,
  SMART_MERCHANDISING_ID,
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
  SMART_SHIPPING_ID,
} from "@src/constants/billing";
import { CheckoutUngatedEmptyGraphic } from "@src/domains/experienceTablePages/CheckoutUngatedEmptyGraphic";
import { ContentUngatedEmptyGraphic } from "@src/domains/experienceTablePages/ContentUngatedEmptyGraphic";
import { EXPERIENCE_MODULE_TABLE_CONFIGS } from "@src/domains/experienceTablePages/experienceModuleConfig";
import { MerchandisingUngatedEmptyGraphic } from "@src/domains/experienceTablePages/MerchandisingUngatedEmptyGraphic";
import { PricingUngatedEmptyGraphic } from "@src/domains/experienceTablePages/PricingUngatedEmptyGraphic";
import { ShippingUngatedEmptyGraphic } from "@src/domains/experienceTablePages/ShippingUngatedEmptyGraphic";
import { MODULE_EMPTY_STATE_COPY } from "@src/features/sharedExperience/components/NoExperiences";
import {
  type LiveModuleTrial,
  useModuleTrials,
} from "@src/features/sharedExperience/hooks/use-module-trials";
import { useDismissAlert } from "@src/hooks/useDismissAlert";
import { EXPERIENCE_ROUTES, SETTINGS_ROUTES } from "@src/routes/routes";
import { useDispatch, useSelector } from "@src/store";
import { getIsAlertDismissed } from "@src/store/slices/appSettings";
import { setConfetti } from "@src/store/slices/global";

import { ModuleTrialWelcomeDialogView } from "./ModuleTrialWelcomeDialog.view";

interface TrialWelcomeModule {
  module: CategoryKey;
  route: string;
  Graphic: ComponentType<SvgIconProps>;
}

const TRIAL_WELCOME_MODULES: Record<string, TrialWelcomeModule> = {
  [SMART_CONTENT_ID]: {
    module: "content",
    route: EXPERIENCE_ROUTES.Experiences_Content,
    Graphic: ContentUngatedEmptyGraphic,
  },
  [SMART_MERCHANDISING_ID]: {
    module: "merchandising",
    route: EXPERIENCE_ROUTES.Experiences_Merchandising,
    Graphic: MerchandisingUngatedEmptyGraphic,
  },
  [SMART_PRICING_LIMITED_ID]: {
    module: "pricing",
    route: EXPERIENCE_ROUTES.Experiences_Pricing,
    Graphic: PricingUngatedEmptyGraphic,
  },
  [SMART_PRICING_UNLIMITED_ID]: {
    module: "pricing",
    route: EXPERIENCE_ROUTES.Experiences_Pricing,
    Graphic: PricingUngatedEmptyGraphic,
  },
  [SMART_SHIPPING_ID]: {
    module: "shipping",
    route: EXPERIENCE_ROUTES.Experiences_Shipping,
    Graphic: ShippingUngatedEmptyGraphic,
  },
  [SMART_CHECKOUT_ID]: {
    module: "checkout",
    route: EXPERIENCE_ROUTES.Experiences_Checkout,
    Graphic: CheckoutUngatedEmptyGraphic,
  },
};

const SECONDS_PER_DAY = 86_400;

const welcomeAlertKey = (trial: LiveModuleTrial) =>
  `module-trial-welcome:${trial.stripePriceId}:${trial.startDate}`;

export function ModuleTrialWelcomeDialog() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const org = useGetOrg();
  const dismissAlert = useDismissAlert();
  const moduleTrials = useModuleTrials();

  const welcomeTrials = moduleTrials
    .filter((trial) => trial.moduleId in TRIAL_WELCOME_MODULES)
    .sort((a, b) => b.startDate - a.startDate);
  const newestTrial = welcomeTrials.at(0);
  const alertKey = newestTrial ? welcomeAlertKey(newestTrial) : "";

  const orgId = org.data?.org?.id;
  const isDismissed = useSelector(getIsAlertDismissed(orgId, alertKey));
  const isOnSettings = pathname.startsWith(SETTINGS_ROUTES.Settings);
  const isOpen = newestTrial !== undefined && !isDismissed && !isOnSettings;

  useEffect(() => {
    if (isOpen) {
      dispatch(setConfetti(true));
    }
  }, [isOpen, dispatch]);

  if (!newestTrial) {
    return null;
  }

  const welcomeModule = TRIAL_WELCOME_MODULES[newestTrial.moduleId];
  const { Graphic } = welcomeModule;

  const onClose = () => {
    for (const trial of welcomeTrials) {
      dismissAlert(welcomeAlertKey(trial));
    }
  };

  return (
    <ModuleTrialWelcomeDialogView
      isOpen={isOpen}
      trialDays={Math.round(
        (newestTrial.endDate - newestTrial.startDate) / SECONDS_PER_DAY
      )}
      moduleDisplayName={
        MODULE_STATIC_CONFIG[newestTrial.moduleId]?.name ?? newestTrial.moduleId
      }
      description={MODULE_EMPTY_STATE_COPY[welcomeModule.module].description}
      graphic={<Graphic sx={{ width: "100%", height: 200 }} />}
      goToModuleLabel={`Go to ${EXPERIENCE_MODULE_TABLE_CONFIGS[welcomeModule.module].pageTitle}`}
      onClose={onClose}
      onGoToModule={() => void navigate(welcomeModule.route)}
    />
  );
}
